"use client";
import Button from "@/components/UI/Button/Button";
import CheckboxInput from "@/components/UI/Input/Checkbox/Checkbox";
import TranslatedLink from "@/components/UI/Link/TranslatedLink";
import Notification from "@/components/UI/Notification/Notification";
import { useDataLayerContext } from "@/datalayer/DataLayerContextProvider";
import { useCurrentLocale, useI18n } from "@/i18n/client";
import { useSessionContext } from "@/lib/auth/SessionContextProvider";
import { useAccountStatusContext } from "@/providers/AccountStatusContextProvider";
import { useCartContext } from "@/providers/CartContextProvider";
import { LoginFormSchema } from "@/schema/login";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@radix-ui/react-form";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as z from "zod";
import FormField from "../FormField/FormField";
import styles from "./loginForm.module.scss";

type FormData = z.infer<typeof LoginFormSchema>;

interface LoginFormProps {
	handleLoginModal?: () => void;
	fullPageLogin?: boolean;
}

export default function LoginForm({
	handleLoginModal,
	fullPageLogin = false,
}: LoginFormProps) {
	const { accountState, setAccountState } = useAccountStatusContext();
	const [isLoading, setIsLoading] = useState(false);

	const { login } = useSessionContext();
	const { setCart } = useCartContext();
	const router = useRouter();
	const t = useI18n();

	const searchParams = useSearchParams();

	const {
		handleSubmit,
		register,
		reset,
		formState: { errors, isSubmitting, isValid, isSubmitted },
		control,
	} = useForm<FormData>({
		resolver: zodResolver(LoginFormSchema),
		defaultValues: {
			email: undefined,
			password: undefined,
			rememberMe: true,
		},
	});

	const datalayer = useDataLayerContext();
	const onSubmit = async (data: FormData) => {
		window.history.replaceState(
			{
				...window.history.state,
				as: window.location.pathname,
				url: window.location.pathname,
			},
			"",
			window.location.pathname,
		);

		setAccountState({
			status: "idle",
		});
		datalayer.formSubmission("login_form");
		const result = await login(data.email, data.password, data.rememberMe);

		if (result.status === "success") {
			setCart(result.cart);
			if (handleLoginModal) {
				handleLoginModal();
			}
			router.push(result.url);
			return;
		}

		const errorMessageMap: Record<string, string> = {
			sessionVersionExpired: t("Auth.Login.sessionExpired"),
			invalidToken: t("Auth.Login.invalidToken"),
			invalidCredentials: t("Auth.Login.invalidCredentials"),
			unknownError: t("Auth.Login.unknownError"),
			sessionExpired: t("Auth.Login.sessionExpired"),
			disabledAccount: t("Auth.Login.disabledAccount"),
			resetPassword: t("Auth.Login.resetPassword"),
		};

		const errorMessage =
			errorMessageMap[result.errorCode] || t("Auth.Login.unknownError");

		setAccountState({
			status: "error",
			message: errorMessage,
			isPermanentBanner: true,
		});

		setIsLoading(false);
	};

	useEffect(() => {
		const error = searchParams.get("error");

		if (!error) {
			return;
		}

		const errorMessageMap: Record<string, string> = {
			sessionVersionExpired: t("Auth.Login.sessionExpired"),
			invalidToken: t("Auth.Login.invalidToken"),
			invalidCredentials: t("Auth.Login.invalidCredentials"),
			unknownError: t("Auth.Login.unknownError"),
			sessionExpired: t("Auth.Login.sessionExpired"),
			disabledAccount: t("Auth.Login.disabledAccount"),
			resetPassword: t("Auth.Login.resetPassword"),
		};

		const errorMessage = errorMessageMap[error] || t("Auth.Login.unknownError");

		setAccountState({
			status: "error",
			message: errorMessage,
			isPermanentBanner: true,
		});
	}, [searchParams, t]);

	useEffect(() => {
		const hasErrors =
			errors.password || errors.email || accountState.status === "error";

		if (hasErrors) {
			setIsLoading(false);
		} else if (isSubmitting || isSubmitted) {
			setIsLoading(true);
		} else {
			setIsLoading(false);
		}

		// Cleanup to reset loading state on component unmount
		return () => setIsLoading(false);
	}, [errors, accountState, isSubmitting, isSubmitted]);

	return (
		<>
			{fullPageLogin ? (
				<>
					<h1 className={styles.title}>{t("Auth.Login.logIn")}</h1>
					{accountState.status === "success" && (
						<Notification
							type='notification'
							variant='success'
							title={t("Notification.success")}
						>
							{accountState.message}
						</Notification>
					)}
				</>
			) : (
				<h2 className={styles.title}>{t("Auth.Login.logIn")}</h2>
			)}

			{accountState.status === "error" && (
				<Notification
					type='notification'
					variant='error'
					title={t("Notification.error")}
				>
					{accountState.message}
				</Notification>
			)}

			<Form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
				<div className={styles.fields}>
					<FormField
						{...register("email")}
						required
						label={t("Auth.Login.email")}
						placeholder={t("Auth.Login.email")}
						disabled={isSubmitting}
						errorMessage={errors.email?.message}
						autoComplete='email'
					/>
					<FormField
						{...register("password")}
						required
						label={t("Auth.Login.password")}
						placeholder={t("Auth.Login.password")}
						type='password'
						disabled={isSubmitting}
						errorMessage={errors.password?.message}
						autoComplete='current-password'
					/>
					<div className={styles.double}>
						<Controller
							control={control}
							name='rememberMe'
							render={({ field }) => (
								<CheckboxInput
									{...field}
									label={t("Auth.Login.rememberMe")}
									value={String(field.value)}
									checked={field.value}
								/>
							)}
						/>

						<TranslatedLink
							href='forgot-password'
							className={styles.forgot}
							onClick={handleLoginModal}
						>
							{t("Auth.Login.forgotPassword")}
						</TranslatedLink>
					</div>
				</div>
				<div className={styles.searchButtons}>
					<Button
						as='button'
						type='submit'
						isLoading={isLoading}
						disabled={isSubmitting || !isValid}
					>
						{t("Auth.Login.logIn")}
					</Button>
					<Button
						as='button'
						type='button'
						variant='tertiary'
						onClick={() => reset()}
					>
						{t("Search.reset")}
					</Button>
				</div>
				{!fullPageLogin && (
					<div className={styles.create}>
						{t("Auth.Login.newToUs")}{" "}
						<TranslatedLink
							href='create-account'
							onClick={handleLoginModal}
							underlined={true}
						>
							{t("Auth.Login.createAccount")}
						</TranslatedLink>
					</div>
				)}
				{/* <DevTool control={control} /> */}
			</Form>
		</>
	);
}
